import { ErrorMessage, useField } from 'formik';
import { memo } from 'react';
import { ChevronDownIcon } from 'lucide-react';

import { CustomErrorMessage } from '@src/design-system/error-message';
import { cn } from '@src/design-system/helpers';
import { inputVariants } from '@src/design-system/input';
import { Currencies } from './currencies';
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandList,
} from '@src/design-system/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@src/design-system/popover';
import { useGetAllLocales } from '@shared/services/get-all-locales';

export const CurrencyField = memo(
  ({
    selectPlaceholder = 'Select currency',
    selectName,
    asInput = true,
    disabled,
  }: {
    selectPlaceholder?: string;
    selectName: string;
    asInput?: boolean;
    disabled?: boolean;
  }) => {
    const [selectFieldProps] = useField(selectName);
    const { data: currencies } = useGetAllLocales();

    const selectedCurrency = currencies?.find(
      (c) => c.currency_code === selectFieldProps.value
    )?.currency_code;

    return (
      <>
        <Popover modal>
          <PopoverTrigger
            className={
              asInput
                ? cn(
                    inputVariants(),
                    'w-full flex justify-between focus-visible:z-10 focus-within:z-10 focus-within:border-l items-center'
                  )
                : `flex gap-2 text-gray-600 font-medium text-sm ${disabled ? 'pointer-events-none opacity-50' : ''}`
            }
          >
            {selectFieldProps.value ? (
              <span className='text-sm '>{selectedCurrency}</span>
            ) : (
              <span className='text-sm text-gray-500'>{selectPlaceholder}</span>
            )}

            <ChevronDownIcon size={20} />
          </PopoverTrigger>
          <PopoverContent
            style={{
              width: 'var(--radix-popover-trigger-width)',
              minWidth: 200,
            }}
            align='end'
            className='p-2'
          >
            <Command>
              <CommandInput placeholder='Search in currencies...' />
              <CommandList>
                <CommandEmpty>No results found.</CommandEmpty>
                <Currencies
                  currencies={currencies || []}
                  name={selectName}
                />
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        <ErrorMessage
          name={selectFieldProps.name}
          component={CustomErrorMessage}
        />
      </>
    );
  }
);
